<template>
  <el-dialog
    v-model="dialogVisible"
    :append-to-body="true"
    :close-on-click-modal="false"
    destroy-on-close
    title="素材库"
    width="900px"
  >
    <div class="media-toolbar">
      <div class="toolbar-left">
        <el-radio-group v-model="currentTab" size="small" @change="onTabChange">
          <el-radio-button value="">全部</el-radio-button>
          <el-radio-button value="IMAGE">图片</el-radio-button>
          <el-radio-button value="VIDEO">视频</el-radio-button>
          <el-radio-button value="FILE">附件</el-radio-button>
        </el-radio-group>
        <el-select
          v-model="groupKey"
          class="group-select"
          clearable
          placeholder="全部分组"
          size="small"
          @change="onGroupChange"
        >
          <el-option label="全部分组" value="" />
          <el-option-group v-if="moduleOptions.length" label="业务模块">
            <el-option
              v-for="m in moduleOptions"
              :key="'m-' + m.code"
              :label="m.title"
              :value="'module:' + m.code"
            />
          </el-option-group>
          <el-option-group v-if="legacyDirs.length" label="历史目录">
            <el-option v-for="d in legacyDirs" :key="'l-' + d" :label="d" :value="'legacy:' + d" />
          </el-option-group>
          <el-option label="未归类" value="none" />
        </el-select>
        <span v-if="usageText" class="usage-hint" :title="usageText">{{ usageText }}</span>
      </div>
      <div class="toolbar-right">
        <el-input
          v-model="searchKey"
          class="search-input"
          clearable
          placeholder="搜索素材"
          size="small"
          @input="onSearch"
        />
        <el-button size="small" type="primary" @click="openUploadPanel"> + 上传素材 </el-button>
      </div>
    </div>

    <div v-if="showUpload" class="upload-area">
      <div class="upload-module-row">
        <span class="label">归属模块</span>
        <el-select
          v-model="uploadModule"
          placeholder="请选择模块"
          size="small"
          style="width: 220px"
          filterable
        >
          <el-option v-for="m in moduleOptions" :key="m.code" :label="m.title" :value="m.code" />
        </el-select>
        <span v-if="resolveUploadDirectory() !== uploadModule" class="dir-hint">
          存储目录 {{ resolveUploadDirectory() }}
        </span>
      </div>
      <el-upload
        ref="uploadRef"
        :auto-upload="false"
        :before-upload="beforeUpload"
        :file-list="uploadFiles"
        :http-request="doUpload"
        :limit="5"
        :on-change="onFileChange"
        :on-exceed="() => ElMessage.warning('一次最多 5 个文件')"
        :show-file-list="true"
        drag
        multiple
      >
        <div class="upload-tip">
          <Icon icon="ep:upload-filled" :size="36" />
          <p>拖拽文件至此 或 点击选择文件</p>
          <p class="upload-limit">新上传会计入 OSS 已用；选用已有不耗费</p>
        </div>
      </el-upload>
      <div class="upload-actions">
        <el-button @click="showUpload = false">取消</el-button>
        <el-button :loading="uploading" type="primary" @click="submitUpload">开始上传</el-button>
      </div>
    </div>

    <div v-loading="loading" class="media-grid-wrapper">
      <el-scrollbar v-if="fileList.length > 0" max-height="400px">
        <div class="media-grid">
          <div
            v-for="item in fileList"
            :key="item.id"
            :class="['media-card', { selected: isSelected(item.id) }]"
            @click="onSelectCard(item)"
          >
            <div class="card-check">
              <el-icon v-if="isSelected(item.id)" color="var(--el-color-primary)" :size="20">
                <CircleCheckFilled />
              </el-icon>
              <el-icon v-else :size="20">
                <CircleCheck />
              </el-icon>
            </div>
            <div class="card-preview">
              <img
                v-if="isImage(item) && item.url && !brokenThumbIds.has(item.id)"
                :src="item.url"
                alt=""
                @error="onThumbError(item.id)"
              />
              <div v-else-if="isVideo(item)" class="video-preview">
                <Icon icon="ep:video-camera-filled" :size="36" />
              </div>
              <div v-else class="file-preview">
                <Icon icon="ep:document" :size="36" />
              </div>
            </div>
            <div class="card-info">
              <span class="card-name" :title="item.name">{{ item.name || item.id }}</span>
              <span class="card-size">{{ formatSize(item.size) }}</span>
            </div>
          </div>
        </div>
      </el-scrollbar>
      <div v-else class="empty-state">
        <Icon icon="ep:picture" :size="48" color="#c0c4cc" />
        <p>{{ loadError ? '素材加载失败，请重试' : '暂无素材，请先上传' }}</p>
        <el-button v-if="loadError" @click="fetchList">重试</el-button>
      </div>
    </div>

    <div class="media-footer">
      <div class="footer-selected">
        <template v-if="selectedList.length > 0"> 已选 {{ selectedList.length }} 项 </template>
      </div>
      <Pagination
        v-model:limit="pageSize"
        v-model:page="pageNo"
        :total="total"
        @pagination="fetchList"
      />
    </div>

    <template #footer>
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button :disabled="selectedList.length === 0" type="primary" @click="onConfirm">
        确定选择
      </el-button>
    </template>
  </el-dialog>
</template>

<script lang="ts" setup>
import { CircleCheck, CircleCheckFilled } from '@element-plus/icons-vue'
import type { UploadRequestOptions } from 'element-plus'
import { getMaterialPage, getMaterialGroups, uploadFileDetail, type FileVO } from '@/api/infra/file'
import * as merchantMaterial from '@/api/merchant/material'
import { getTenantProducts, type ProductVO } from '@/api/system/tenantModule'
import { formatBytes, getOssCapacityUsage } from '@/api/system/ossCapacity'
import { ElMessageBox } from 'element-plus'

defineOptions({ name: 'TenantMaterialSelector' })

const router = useRouter()
const route = useRoute()
const isMerchant = computed(() => route.path.startsWith('/store/'))
const loadError = ref(false)
const OSS_BUY_PATH = '/system/oss-capacity'

interface Props {
  modelValue: boolean
  /** 默认媒体大类 */
  mediaType?: 'IMAGE' | 'VIDEO' | 'FILE' | ''
  multiple?: boolean
  /** 打开时默认业务模块码；也可传路径如 shop/banner（归属取首段 shop，上传 directory 仍用完整路径） */
  defaultModule?: string
  /** 单文件大小上限 MB */
  fileSizeMb?: number
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  mediaType: '',
  multiple: false,
  defaultModule: '',
  fileSizeMb: 10
})

const emit = defineEmits<{
  (e: 'update:modelValue', val: boolean): void
  /** 选用已有：不耗 OSS；仅回传已有文件信息 */
  (e: 'confirm', items: FileVO[]): void
}>()

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const currentTab = ref<'IMAGE' | 'VIDEO' | 'FILE' | ''>('IMAGE')
const searchKey = ref('')
const loading = ref(false)
const showUpload = ref(false)
const uploading = ref(false)
const uploadRef = ref()
const uploadFiles = ref<any[]>([])
const groupKey = ref('')
const uploadModule = ref('')
const moduleOptions = ref<{ code: string; title: string }[]>([])
const legacyDirs = ref<string[]>([])

const pageNo = ref(1)
const pageSize = ref(20)
const total = ref(0)
const fileList = ref<FileVO[]>([])
const selectedList = ref<FileVO[]>([])
const usageText = ref('')
/** 缩略图加载失败的 id，避免白块无反馈 */
const brokenThumbIds = ref<Set<number>>(new Set())

let searchTimer: ReturnType<typeof setTimeout> | null = null

/**
 * 与后端 FileBizModuleUtils.resolveFromDirectory 对齐：
 * directory 可带路径（如 shop/banner），biz_module 只取首段（shop）。
 */
const resolveBizModuleCode = (raw?: string) => {
  if (!raw) return ''
  let n = raw.trim().replace(/\\/g, '/')
  while (n.startsWith('/')) n = n.slice(1)
  while (n.endsWith('/')) n = n.slice(0, -1)
  if (!n) return ''
  const first = n.split('/')[0] || ''
  if (!first || /^\d{8}$/.test(first)) return ''
  return first
}

/** 路径段 → 产品 moduleCode 别名（页面 path 与产品码不完全一致时） */
const PATH_MODULE_ALIASES: Record<string, string> = {
  shop: 'mall',
  product: 'mall',
  spu: 'mall',
  sku: 'mall',
  merchant: 'mall',
  catering: 'food',
  'food-merchant': 'food',
  canteen: 'food',
  hotel: 'hotel',
  scenic: 'scenic',
  ticketing: 'ticket',
  ticket: 'ticket',
  visiting: 'visiting',
  errand: 'errand',
  mp: 'mp',
  cms: 'cms',
  ai: 'ai'
}

/**
 * 从当前路由识别业务模块（页面未传 directory 时用）。
 * 优先级：matched.meta.moduleCode → path 段命中已开通产品码 → 常见别名。
 * 禁止用「产品列表第一项」瞎猜（会落到跑腿等无关模块）。
 */
const inferModuleFromRoute = (knownCodes: string[] = []) => {
  const knownLower = new Map(knownCodes.map((c) => [c.toLowerCase(), c]))
  const pick = (code: string) => {
    if (!code) return ''
    return knownLower.get(code.toLowerCase()) || code
  }

  for (let i = route.matched.length - 1; i >= 0; i--) {
    const metaCode = route.matched[i]?.meta?.moduleCode
    if (typeof metaCode === 'string' && metaCode.trim()) {
      return pick(metaCode.trim())
    }
  }

  const parts = route.path.toLowerCase().split('/').filter(Boolean)
  for (const part of parts) {
    if (knownLower.has(part)) {
      return knownLower.get(part) || part
    }
  }
  for (const part of parts) {
    const alias = PATH_MODULE_ALIASES[part]
    if (alias) {
      return pick(alias)
    }
  }
  return ''
}

/** 页面 directory 优先，否则按路由自识别 */
const resolveContextModuleCode = (knownCodes: string[] = []) => {
  return resolveBizModuleCode(props.defaultModule) || inferModuleFromRoute(knownCodes)
}

/**
 * 上传 directory：若页面传入 shop/banner 且当前归属仍是 shop，保留完整路径写 OSS；
 * 用户改选其它模块则只用模块码。
 */
const resolveUploadDirectory = () => {
  const mod = uploadModule.value
  const def = (props.defaultModule || '').trim()
  if (def && resolveBizModuleCode(def) === mod) {
    return def
  }
  // 无显式 directory 时：用模块码；商城商品等可写成 mall/product
  return mod
}

const loadUsage = async () => {
  if (isMerchant.value) {
    usageText.value = ''
    return
  }
  try {
    const u = await getOssCapacityUsage()
    if (!u || u.unlimited) {
      usageText.value = ''
      return
    }
    usageText.value = `已用 ${formatBytes(u.ossUsed)} / 配额 ${formatBytes(u.ossQuota)}`
  } catch {
    usageText.value = ''
  }
}

const promptOssExpand = async (rawMsg?: string) => {
  if (isMerchant.value) {
    ElMessage.error(rawMsg || '素材上传容量不足，请联系平台管理员扩容。')
    return
  }
  try {
    await ElMessageBox.confirm(
      rawMsg || 'OSS 容量不足，无法上传新文件。选用库内已有素材不耗费容量。',
      '容量不足',
      {
        type: 'warning',
        confirmButtonText: '去扩容',
        cancelButtonText: '取消'
      }
    )
    dialogVisible.value = false
    await router.push({ path: OSS_BUY_PATH })
  } catch {
    // 用户取消
  }
}

const isQuotaError = (e: any) => {
  const msg = String(e?.message || e?.msg || '')
  const code = e?.code ?? e?.data?.code
  return code === 1002015030 || msg.includes('OSS') || msg.includes('配额') || msg.includes('容量')
}

const onSearch = () => {
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    pageNo.value = 1
    fetchList()
  }, 300)
}

const onTabChange = () => {
  pageNo.value = 1
  fetchList()
}

const onGroupChange = () => {
  pageNo.value = 1
  fetchList()
}

const buildQuery = () => {
  const params: any = {
    pageNo: pageNo.value,
    pageSize: pageSize.value,
    name: searchKey.value || undefined
  }
  // 空 = 全部类型，与文件列表口径一致（仍限本租户）
  if (currentTab.value) {
    params.mediaCategory = currentTab.value
  }
  if (groupKey.value.startsWith('module:')) {
    params.bizModule = groupKey.value.slice('module:'.length)
  } else if (groupKey.value.startsWith('legacy:')) {
    params.legacyDir = groupKey.value.slice('legacy:'.length)
  } else if (groupKey.value === 'none') {
    params.bizModule = '__none__'
  }
  return params
}

const fetchList = async () => {
  loading.value = true
  try {
    loadError.value = false
    const data = await (isMerchant.value ? merchantMaterial.getMaterialPage : getMaterialPage)(
      buildQuery()
    )
    fileList.value = data.list || []
    total.value = data.total || 0
  } catch {
    fileList.value = []
    total.value = 0
    loadError.value = true
  } finally {
    loading.value = false
  }
}

const loadGroups = async () => {
  const normalizeProductList = (raw: unknown): ProductVO[] => {
    if (Array.isArray(raw)) return raw as ProductVO[]
    if (raw && typeof raw === 'object') {
      const o = raw as Record<string, unknown>
      if (Array.isArray(o.list)) return o.list as ProductVO[]
      if (Array.isArray(o.data)) return o.data as ProductVO[]
    }
    return []
  }

  /** 保证归属下拉有当前码，并写入 uploadModule（避免 el-select 有值无 option 时显示成占位「请选择模块」） */
  const finalizeUploadModule = () => {
    const known = moduleOptions.value.map((m) => m.code)
    // 1) 页面 directory  2) 当前路由识别  3) 通用 —— 绝不取产品列表第一项（易错成跑腿）
    let code = resolveContextModuleCode(known) || 'common'
    const hit = moduleOptions.value.find((m) => m.code === code)
    if (!hit) {
      moduleOptions.value = [
        { code, title: code === 'common' ? '通用' : code },
        ...moduleOptions.value
      ]
    }
    uploadModule.value = code
  }

  try {
    const [productsRaw, groups] = await Promise.all([
      isMerchant.value
        ? Promise.resolve([] as ProductVO[])
        : getTenantProducts().catch(() => [] as ProductVO[]),
      (isMerchant.value ? merchantMaterial.getMaterialGroups() : getMaterialGroups()).catch(() => ({
        legacyDirs: [] as string[]
      }))
    ])
    const products = normalizeProductList(productsRaw)
    const opened = products.filter((p) =>
      ['ACTIVE', 'TRIAL'].includes(String(p?.tenantStatus || ''))
    )
    moduleOptions.value = opened
      .filter((p) => !!p?.moduleCode)
      .map((p) => ({
        code: String(p.moduleCode),
        title: p.title || p.moduleCode
      }))
    // 无开通模块时仍允许上传到「通用」，避免卡死
    if (!moduleOptions.value.some((m) => m.code === 'common')) {
      moduleOptions.value.push({ code: 'common', title: '通用' })
    }
    const moduleCodes = new Set(moduleOptions.value.map((m) => m.code))
    legacyDirs.value = (groups?.legacyDirs || []).filter((d) => !moduleCodes.has(d))
    // 页面 directory（如 shop/banner）→ 归属首段 shop；必须先有 option 再赋 v-model
    finalizeUploadModule()
  } catch {
    if (!moduleOptions.value.length) {
      moduleOptions.value = [{ code: 'common', title: '通用' }]
    }
    finalizeUploadModule()
  }
}

/** 点「+ 上传素材」时再兜一次，防止异步未完成或 option 被清空 */
const openUploadPanel = () => {
  const known = moduleOptions.value.map((m) => m.code)
  const code = resolveContextModuleCode(known) || uploadModule.value || 'common'
  if (!moduleOptions.value.some((m) => m.code === code)) {
    moduleOptions.value = [
      { code, title: code === 'common' ? '通用' : code },
      ...moduleOptions.value
    ]
  }
  uploadModule.value = code
  showUpload.value = !showUpload.value
}

const isImage = (item: FileVO) => (item.type || '').startsWith('image/')
const isVideo = (item: FileVO) => (item.type || '').startsWith('video/')

const onThumbError = (id?: number) => {
  if (id == null) return
  const next = new Set(brokenThumbIds.value)
  next.add(id)
  brokenThumbIds.value = next
}

const isSelected = (id: number) => selectedList.value.some((i) => i.id === id)

const onSelectCard = (item: FileVO) => {
  if (isSelected(item.id)) {
    selectedList.value = selectedList.value.filter((i) => i.id !== item.id)
  } else if (props.multiple) {
    selectedList.value.push(item)
  } else {
    selectedList.value = [item]
  }
}

const onConfirm = () => {
  // 只回传已有文件，禁止在此触发上传
  emit('confirm', [...selectedList.value])
  dialogVisible.value = false
  selectedList.value = []
}

const beforeUpload = (file: File) => {
  const max = props.fileSizeMb * 1024 * 1024
  if (file.size > max) {
    ElMessage.warning(`文件 ${file.name} 超过 ${props.fileSizeMb}MB 限制`)
    return false
  }
  return true
}

const onFileChange = (_file: any, files: any[]) => {
  uploadFiles.value = files
}

const doUpload = async (options: UploadRequestOptions) => {
  if (!uploadModule.value) {
    ElMessage.warning('请先选择归属模块')
    return
  }
  const form = {
    file: options.file,
    directory: resolveUploadDirectory()
  }
  try {
    const res = await (isMerchant.value ? merchantMaterial.uploadFileDetail : uploadFileDetail)(
      form
    )
    const data = (res as any)?.url ? res : (res as any)?.data || res
    options.onSuccess?.(data as any)
  } catch (e: any) {
    if (isQuotaError(e)) {
      await promptOssExpand(e?.message || e?.msg)
    }
    options.onError?.(e as any)
  }
}

const submitUpload = async () => {
  if (!uploadModule.value) {
    ElMessage.warning('请先选择归属模块再上传')
    return
  }
  const rawFiles = uploadFiles.value.map((f) => f.raw).filter(Boolean) as File[]
  if (!rawFiles.length) {
    ElMessage.warning('请先选择文件')
    return
  }
  uploading.value = true
  const uploaded: FileVO[] = []
  const directory = resolveUploadDirectory()
  try {
    for (const file of rawFiles) {
      const res = await (isMerchant.value ? merchantMaterial.uploadFileDetail : uploadFileDetail)({
        file,
        directory
      })
      const data = (res as any)?.url ? res : (res as any)?.data || res
      const biz = data.bizModule || resolveBizModuleCode(directory) || uploadModule.value
      uploaded.push({
        id: Number(data.id),
        url: data.url,
        name: data.name || file.name,
        size: data.size,
        type: data.type,
        bizModule: biz
      })
    }
    ElMessage.success(`已上传 ${uploaded.length} 个文件（计入 OSS 已用）`)
    showUpload.value = false
    uploadFiles.value = []
    uploadRef.value?.clearFiles?.()
    // 按服务端 biz_module 筛（shop），禁止用完整 directory（shop/banner）否则列表为空
    const bizForFilter =
      uploaded[0]?.bizModule || resolveBizModuleCode(directory) || uploadModule.value
    groupKey.value = 'module:' + bizForFilter
    pageNo.value = 1
    brokenThumbIds.value = new Set()
    await fetchList()
    // 优先用列表回查结果勾选（url 与列表一致）；找不到再用上传回包
    const idSet = new Set(uploaded.map((u) => u.id))
    const fromList = fileList.value.filter((f) => idSet.has(f.id))
    const pick = fromList.length ? fromList : uploaded
    if (props.multiple) {
      const map = new Map(selectedList.value.map((i) => [i.id, i]))
      pick.forEach((u) => map.set(u.id, u))
      selectedList.value = [...map.values()]
    } else if (pick.length) {
      selectedList.value = [pick[pick.length - 1]]
    }
  } catch (e: any) {
    if (isQuotaError(e)) {
      await promptOssExpand(e?.message || e?.msg)
    } else {
      ElMessage.error(String(e?.message || e?.msg || '上传失败'))
    }
  } finally {
    uploading.value = false
  }
}

const formatSize = (bytes?: number) => {
  if (!bytes) return '-'
  if (bytes < 1024) return bytes + 'B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'KB'
  return (bytes / (1024 * 1024)).toFixed(1) + 'MB'
}

watch(dialogVisible, (val) => {
  if (val) {
    // mediaType='' 表示「全部」；仅未传时默认图片
    currentTab.value = (props.mediaType ?? 'IMAGE') as any
    pageNo.value = 1
    searchKey.value = ''
    selectedList.value = []
    showUpload.value = false
    groupKey.value = ''
    usageText.value = ''
    // 同步预填：页面 directory 或当前路由模块（如 /mall/product → mall）
    const early = resolveContextModuleCode([])
    if (early) {
      moduleOptions.value = [{ code: early, title: early === 'common' ? '通用' : early }]
      uploadModule.value = early
    }
    loadUsage()
    loadGroups().then(() => fetchList())
  }
})
</script>

<style scoped>
.media-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 12px;
}
.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.search-input {
  width: 180px;
}
.group-select {
  width: 160px;
}
.usage-hint {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}
.upload-area {
  margin-bottom: 12px;
  padding: 12px;
  border: 1px dashed var(--el-border-color);
  border-radius: 8px;
  background: var(--el-fill-color-blank);
}
.upload-module-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.upload-module-row .label {
  font-size: 13px;
  color: var(--el-text-color-secondary);
}
.upload-module-row .dir-hint {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  white-space: nowrap;
}
.upload-tip {
  text-align: center;
  color: var(--el-text-color-secondary);
}
.upload-limit {
  font-size: 12px;
  margin-top: 4px;
}
.upload-actions {
  margin-top: 8px;
  text-align: right;
}
.media-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  padding: 4px;
}
.media-card {
  position: relative;
  border: 1px solid var(--el-border-color);
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
}
.media-card.selected {
  border-color: var(--el-color-primary);
  box-shadow: 0 0 0 1px var(--el-color-primary);
}
.card-check {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 1;
  background: #fff;
  border-radius: 50%;
}
.card-preview {
  height: 110px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  overflow: hidden;
}
.card-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.card-info {
  padding: 6px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.card-name {
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.card-size {
  font-size: 11px;
  color: var(--el-text-color-secondary);
}
.empty-state {
  padding: 48px 0;
  text-align: center;
  color: #c0c4cc;
}
.media-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
}
.footer-selected {
  font-size: 13px;
  color: var(--el-text-color-secondary);
}
.video-preview,
.file-preview {
  color: var(--el-text-color-secondary);
}
</style>
